import { useState } from "react";

interface Review {
  id: string;
  author: string;
  avatar: string;
  date: string;
  rating: number;
  title: string;
  comment: string;
  stayType: string;
  helpful: number;
  categories: {
    cleanliness: number;
    location: number;
    service: number;
    value: number;
  };
}

const MOCK_REVIEWS: Review[] = [
  {
    id: "r1",
    author: "Selin Kaya",
    avatar: "SK",
    date: "Mart 2026",
    rating: 5,
    title: "Hayatımın en güzel tatil deneyimi!",
    comment: "Buraya gelmeden önce çok araştırdım ve hiç pişman olmadım. Personel son derece ilgili ve samimi, odalar tertemiz ve konforlu. Kahvaltı muhteşemdi, her sabah farklı lezzetler sunuldu. Kesinlikle tekrar geleceğim ve herkese tavsiye ediyorum.",
    stayType: "Çift",
    helpful: 12,
    categories: { cleanliness: 5, location: 5, service: 5, value: 4 },
  },
  {
    id: "r2",
    author: "Ahmet Demir",
    avatar: "AD",
    date: "Şubat 2026",
    rating: 4,
    title: "Konumu ve manzarası mükemmel",
    comment: "Konum gerçekten eşsiz, her yerden harika manzara var. Odalar temiz ve düzenli. Tek eksik WiFi hızının biraz yavaş olması. Ama genel olarak çok memnun kaldım, fiyat-performans açısından da gayet iyi.",
    stayType: "Aile",
    helpful: 8,
    categories: { cleanliness: 4, location: 5, service: 4, value: 4 },
  },
  {
    id: "r3",
    author: "Marie Dubois",
    avatar: "MD",
    date: "Ocak 2026",
    rating: 5,
    title: "Absolutely wonderful experience",
    comment: "We stayed for 4 nights and it was perfect. The staff speaks excellent English and was incredibly helpful with local recommendations. The breakfast was amazing with fresh local products. The view from our room was breathtaking. Will definitely return!",
    stayType: "Çift",
    helpful: 15,
    categories: { cleanliness: 5, location: 5, service: 5, value: 5 },
  },
  {
    id: "r4",
    author: "Burak Yıldız",
    avatar: "BY",
    date: "Aralık 2025",
    rating: 4,
    title: "Rahat ve huzurlu bir konaklama",
    comment: "Şehrin gürültüsünden uzak, sakin bir ortamda dinlenmek istedik ve tam istediğimizi bulduk. Personel çok yardımsever. Odalar biraz küçük ama temiz ve düzenli. Kahvaltı çeşitliliği artırılabilir.",
    stayType: "Çift",
    helpful: 5,
    categories: { cleanliness: 4, location: 4, service: 5, value: 3 },
  },
  {
    id: "r5",
    author: "Zeynep Arslan",
    avatar: "ZA",
    date: "Kasım 2025",
    rating: 3,
    title: "Ortalama bir deneyim",
    comment: "Beklentilerimi tam karşılamadı. Oda fotoğraflardaki kadar büyük değildi. Personel ilgili ama check-in biraz uzun sürdü. Konum iyi, çevreye ulaşım kolay. Fiyatı düşünüldüğünde makul sayılabilir.",
    stayType: "Solo",
    helpful: 3,
    categories: { cleanliness: 3, location: 4, service: 3, value: 3 },
  },
];

const StarRating = ({ value, onChange, size = "md" }: { value: number; onChange?: (v: number) => void; size?: "sm" | "md" | "lg" }) => {
  const [hovered, setHovered] = useState(0);
  const sizeClass = size === "sm" ? "text-sm" : size === "lg" ? "text-2xl" : "text-lg";

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange?.(star)}
          onMouseEnter={() => onChange && setHovered(star)}
          onMouseLeave={() => onChange && setHovered(0)}
          className={`${sizeClass} transition-colors ${onChange ? "cursor-pointer" : "cursor-default"}`}
        >
          <i className={`${
            star <= (hovered || value) ? "ri-star-fill text-amber-400" : "ri-star-line text-stone-300"
          }`}></i>
        </button>
      ))}
    </div>
  );
};

const RatingBar = ({ label, value }: { label: string; value: number }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs text-stone-500 w-20 flex-shrink-0">{label}</span>
    <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
      <div
        className="h-full bg-amber-400 rounded-full transition-all duration-500"
        style={{ width: `${(value / 5) * 100}%` }}
      ></div>
    </div>
    <span className="text-xs font-semibold text-stone-700 w-6 text-right">{value.toFixed(1)}</span>
  </div>
);

interface ReviewsSectionProps {
  accommodationId: string;
  accommodationName: string;
}

const ReviewsSection = ({ accommodationId, accommodationName }: ReviewsSectionProps) => {
  const storageKey = `reviews_${accommodationId}`;
  const [reviews, setReviews] = useState<Review[]>(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(storageKey) || "[]");
      return [...MOCK_REVIEWS, ...stored];
    } catch {
      return MOCK_REVIEWS;
    }
  });

  const [showForm, setShowForm] = useState(false);
  const [sortBy, setSortBy] = useState<"recent" | "highest" | "lowest">("recent");
  const [helpfulClicked, setHelpfulClicked] = useState<Set<string>>(new Set());

  const [form, setForm] = useState({
    author: "",
    email: "",
    stayType: "Çift",
    rating: 0,
    title: "",
    comment: "",
    cleanliness: 0,
    location: 0,
    service: 0,
    value: 0,
  });
  const [formError, setFormError] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const avgRating = reviews.reduce((s, r) => s + r.rating, 0) / reviews.length;
  const avgCategories = {
    cleanliness: reviews.reduce((s, r) => s + r.categories.cleanliness, 0) / reviews.length,
    location: reviews.reduce((s, r) => s + r.categories.location, 0) / reviews.length,
    service: reviews.reduce((s, r) => s + r.categories.service, 0) / reviews.length,
    value: reviews.reduce((s, r) => s + r.categories.value, 0) / reviews.length,
  };

  const ratingDist = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: reviews.filter((r) => r.rating === star).length,
    pct: (reviews.filter((r) => r.rating === star).length / reviews.length) * 100,
  }));

  const sorted = [...reviews].sort((a, b) => {
    if (sortBy === "highest") return b.rating - a.rating;
    if (sortBy === "lowest") return a.rating - b.rating;
    return 0;
  });

  const handleHelpful = (id: string) => {
    if (helpfulClicked.has(id)) return;
    setHelpfulClicked((prev) => new Set([...prev, id]));
    setReviews((prev) => prev.map((r) => r.id === id ? { ...r, helpful: r.helpful + 1 } : r));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (form.rating === 0) { setFormError("Lütfen genel puanınızı seçin."); return; }
    if (!form.author.trim()) { setFormError("Lütfen adınızı girin."); return; }
    if (!form.comment.trim() || form.comment.length < 20) { setFormError("Yorum en az 20 karakter olmalıdır."); return; }
    setFormError("");

    const newReview: Review = {
      id: `user-${Date.now()}`,
      author: form.author,
      avatar: form.author.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2),
      date: new Date().toLocaleDateString("tr-TR", { month: "long", year: "numeric" }),
      rating: form.rating,
      title: form.title || "Konaklama Deneyimim",
      comment: form.comment,
      stayType: form.stayType,
      helpful: 0,
      categories: {
        cleanliness: form.cleanliness || form.rating,
        location: form.location || form.rating,
        service: form.service || form.rating,
        value: form.value || form.rating,
      },
    };

    const stored = JSON.parse(localStorage.getItem(storageKey) || "[]");
    stored.push(newReview);
    localStorage.setItem(storageKey, JSON.stringify(stored));
    setReviews((prev) => [newReview, ...prev]);
    setSubmitted(true);
    setShowForm(false);
    setForm({ author: "", email: "", stayType: "Çift", rating: 0, title: "", comment: "", cleanliness: 0, location: 0, service: 0, value: 0 });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          Misafir Yorumları
        </h2>
        <button
          onClick={() => { setShowForm(!showForm); setSubmitted(false); }}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-edit-line"></i>
          Yorum Yaz
        </button>
      </div>

      {/* Rating Summary */}
      <div className="bg-stone-50 rounded-2xl p-6 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Overall */}
          <div className="flex items-center gap-5">
            <div className="text-center">
              <div className="text-5xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                {avgRating.toFixed(1)}
              </div>
              <StarRating value={Math.round(avgRating)} size="sm" />
              <p className="text-xs text-stone-400 mt-1">{reviews.length} yorum</p>
            </div>
            <div className="flex-1 space-y-1.5">
              {ratingDist.map(({ star, count, pct }) => (
                <div key={star} className="flex items-center gap-2">
                  <span className="text-xs text-stone-500 w-3">{star}</span>
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                    <i className="ri-star-fill text-amber-400 text-xs"></i>
                  </div>
                  <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="text-xs text-stone-400 w-4 text-right">{count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Category Ratings */}
          <div className="space-y-3">
            <RatingBar label="Temizlik" value={avgCategories.cleanliness} />
            <RatingBar label="Konum" value={avgCategories.location} />
            <RatingBar label="Hizmet" value={avgCategories.service} />
            <RatingBar label="Fiyat/Değer" value={avgCategories.value} />
          </div>
        </div>
      </div>

      {/* Success message */}
      {submitted && (
        <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-5">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 rounded-full flex-shrink-0">
            <i className="ri-checkbox-circle-fill text-emerald-500"></i>
          </div>
          <div>
            <p className="text-sm font-semibold text-emerald-800">Yorumunuz eklendi!</p>
            <p className="text-xs text-emerald-600">Deneyiminizi paylaştığınız için teşekkürler.</p>
          </div>
        </div>
      )}

      {/* Review Form */}
      {showForm && (
        <div className="bg-white border border-amber-200 rounded-2xl p-6 mb-6">
          <h3 className="font-bold text-stone-900 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            {accommodationName} için Yorum Yaz
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Overall Rating */}
            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-2">Genel Puan *</label>
              <div className="flex items-center gap-3">
                <StarRating value={form.rating} onChange={(v) => setForm((p) => ({ ...p, rating: v }))} size="lg" />
                <span className="text-sm text-stone-500">
                  {form.rating === 0 ? "Puan seçin" : form.rating === 5 ? "Mükemmel!" : form.rating === 4 ? "Çok İyi" : form.rating === 3 ? "İyi" : form.rating === 2 ? "Orta" : "Kötü"}
                </span>
              </div>
            </div>

            {/* Category Ratings */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-stone-50 rounded-xl">
              <p className="col-span-2 text-xs font-semibold text-stone-500 uppercase tracking-wider">Kategori Puanları (isteğe bağlı)</p>
              {[
                { key: "cleanliness", label: "Temizlik" },
                { key: "location", label: "Konum" },
                { key: "service", label: "Hizmet" },
                { key: "value", label: "Fiyat/Değer" },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-xs text-stone-500 mb-1">{label}</label>
                  <StarRating
                    value={form[key as keyof typeof form] as number}
                    onChange={(v) => setForm((p) => ({ ...p, [key]: v }))}
                    size="sm"
                  />
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Adınız *</label>
                <input
                  type="text"
                  value={form.author}
                  onChange={(e) => setForm((p) => ({ ...p, author: e.target.value }))}
                  placeholder="Ad Soyad"
                  className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">Konaklama Türü</label>
                <select
                  value={form.stayType}
                  onChange={(e) => setForm((p) => ({ ...p, stayType: e.target.value }))}
                  className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 bg-white"
                >
                  {["Çift", "Aile", "Solo", "İş Seyahati", "Arkadaş Grubu"].map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yorum Başlığı</label>
              <input
                type="text"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder="Deneyiminizi özetleyin..."
                className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">Yorumunuz *</label>
              <textarea
                rows={4}
                maxLength={500}
                value={form.comment}
                onChange={(e) => setForm((p) => ({ ...p, comment: e.target.value }))}
                placeholder="Konaklama deneyiminizi diğer misafirlerle paylaşın... (en az 20 karakter)"
                className="w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors resize-none"
              />
              <p className="text-xs text-stone-400 text-right mt-1">{form.comment.length}/500</p>
            </div>

            {formError && (
              <div className="flex items-center gap-2 text-rose-600 text-sm bg-rose-50 rounded-xl px-3 py-2">
                <i className="ri-error-warning-line"></i>
                {formError}
              </div>
            )}

            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-send-plane-line"></i> Yorumu Gönder
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-5 py-3 border border-stone-200 text-stone-600 text-sm font-medium rounded-xl hover:bg-stone-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                İptal
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sort */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-stone-500">{reviews.length} yorum</p>
        <div className="flex items-center gap-1 bg-stone-100 rounded-xl p-1">
          {[
            { key: "recent", label: "En Yeni" },
            { key: "highest", label: "En Yüksek" },
            { key: "lowest", label: "En Düşük" },
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setSortBy(key as typeof sortBy)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                sortBy === key ? "bg-white text-stone-800" : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Review Cards */}
      <div className="space-y-4">
        {sorted.map((review) => (
          <div key={review.id} className="bg-white border border-stone-100 rounded-2xl p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <div>
                  <p className="font-semibold text-stone-900 text-sm">{review.author}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-stone-400">{review.date}</span>
                    <span className="text-stone-200">·</span>
                    <span className="text-xs text-stone-400">{review.stayType}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <StarRating value={review.rating} size="sm" />
                <span className="text-sm font-bold text-stone-700">{review.rating}.0</span>
              </div>
            </div>

            {review.title && (
              <h4 className="font-semibold text-stone-900 text-sm mb-2">{review.title}</h4>
            )}
            <p className="text-stone-600 text-sm leading-relaxed mb-3">{review.comment}</p>

            {/* Category mini bars */}
            <div className="grid grid-cols-4 gap-2 mb-3 p-3 bg-stone-50 rounded-xl">
              {[
                { label: "Temizlik", val: review.categories.cleanliness },
                { label: "Konum", val: review.categories.location },
                { label: "Hizmet", val: review.categories.service },
                { label: "Değer", val: review.categories.value },
              ].map(({ label, val }) => (
                <div key={label} className="text-center">
                  <p className="text-xs text-stone-400 mb-1">{label}</p>
                  <div className="flex items-center justify-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <div key={s} className={`w-1.5 h-1.5 rounded-full ${s <= val ? "bg-amber-400" : "bg-stone-200"}`}></div>
                    ))}
                  </div>
                  <p className="text-xs font-semibold text-stone-600 mt-1">{val}.0</p>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleHelpful(review.id)}
                className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  helpfulClicked.has(review.id)
                    ? "bg-amber-100 text-amber-700 font-semibold"
                    : "bg-stone-100 text-stone-500 hover:bg-stone-200"
                }`}
              >
                <i className="ri-thumb-up-line"></i>
                Faydalı ({review.helpful})
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewsSection;
